// @ts-check
import { FileText, Pencil, RefreshCw, ExternalLink, Sparkles } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ChatMarkdown } from "./ChatBubble"
import { OPEN_CANVAS_PANEL_EVENT } from "@/components/canvas/CanvasPanel"
import { getParsedToolParam } from "./tools"
import {
  CreateCanvasArgsSchema, CreateCanvasResultSchema,
  EditDocumentArgsSchema, EditDocumentResultSchema,
  WriteDocumentArgsSchema, WriteDocumentResultSchema,
} from "./tools/canvas"

const CANVAS_SCHEMAS = {
  create_canvas: { args: CreateCanvasArgsSchema, result: CreateCanvasResultSchema },
  edit_document: { args: EditDocumentArgsSchema, result: EditDocumentResultSchema },
  write_document: { args: WriteDocumentArgsSchema, result: WriteDocumentResultSchema },
}

/** @param {string} documentId */
function openCanvas(documentId) {
  document.dispatchEvent(new CustomEvent(OPEN_CANVAS_PANEL_EVENT, { detail: { documentId } }))
}

/**
 * Pull the resulting documentId out of a canvas tool call's result, if any.
 * @param {{ name: string, args: string, result: string | null }} part
 */
export function extractCanvasDocumentId({ name, args: rawArgs, result: rawResult }) {
  const schema = CANVAS_SCHEMAS[name]
  if (!schema) return null
  const args = getParsedToolParam(rawArgs, schema.args)
  const result = getParsedToolParam(rawResult, schema.result)
  if (!args || args instanceof Error) return null
  if (result == null || result instanceof Error) return null
  return result?.document?._id ?? null
}

/**
 * Single consolidated CTA rendered once per response, in place of a preview
 * card per edit — clicking opens the canvas so the user reviews the AI's
 * suggestions there rather than in a truncated in-bubble diff preview.
 * @param {{ documentId: string }} props
 */
export function CanvasSuggestionsButton({ documentId }) {
  if (!documentId) return null
  return (
    <div className="my-3">
      <Button
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 text-xs"
        onClick={() => openCanvas(documentId)}
      >
        <Sparkles size={13} />
        Click to see AI suggestions
      </Button>
    </div>
  )
}

/**
 * Rich canvas card rendered directly in the bubble (below the text response).
 * @param {{ name: string, args: string, result: string | null }} props
 */
export default function ChatBubbleCanvas({ name, args: rawArgs, result: rawResult }) {
  const schema = CANVAS_SCHEMAS[name]
  if (!schema) return null

  const args = getParsedToolParam(rawArgs, schema.args)
  const result = getParsedToolParam(rawResult, schema.result)
  if (!args || args instanceof Error) return null

  const isSuccess = result != null && !(result instanceof Error)
  const documentId = isSuccess ? result?.document?._id : null

  if (name === "create_canvas") {
    return <CreateCanvasCard args={args} documentId={documentId} />
  }
  if (name === "edit_document") {
    const title = isSuccess ? result?.document?.title : null
    return <EditDocumentCard args={args} title={title} documentId={documentId} />
  }
  if (name === "write_document") {
    const title = isSuccess ? result?.document?.title : null
    return <WriteDocumentCard args={args} title={title} documentId={documentId} />
  }
  return null
}

// ─── Shared ───────────────────────────────────────────────────────────────────

/** Clipped markdown preview with a fade overlay at the bottom. */
function ContentPreview({ content, maxHeight = 120, topFade = false }) {
  return (
    <div className="relative overflow-hidden" style={{ maxHeight }}>
      {topFade && (
        <div className="absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-background to-transparent pointer-events-none z-10" />
      )}
      <div className="px-4 pt-3 pb-6 prose prose-xs dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
        <ChatMarkdown>{content}</ChatMarkdown>
      </div>
      <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </div>
  )
}

function OpenButton({ documentId }) {
  if (!documentId) return null
  return (
    <div className="px-3 py-2 border-t border-border/50">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 gap-1.5 text-xs w-full justify-start"
        onClick={() => openCanvas(documentId)}
      >
        <ExternalLink size={12} />
        Open in canvas
      </Button>
    </div>
  )
}

// ─── Create ──────────────────────────────────────────────────────────────────

function CreateCanvasCard({ args, documentId }) {
  return (
    <div className="my-3 rounded-lg border border-border overflow-hidden bg-background">
      <div className="px-3 py-2 border-b border-border/50 flex items-center gap-2 text-sm">
        <FileText size={14} className="text-muted-foreground shrink-0" />
        <span className="font-medium truncate">{args.title}</span>
      </div>
      {args.content && <ContentPreview content={args.content} maxHeight={140} />}
      <OpenButton documentId={documentId} />
    </div>
  )
}

// ─── Edit ─────────────────────────────────────────────────────────────────────

function EditDocumentCard({ args, title, documentId }) {
  const edits = args.edits ?? []

  return (
    <div className="my-3 rounded-lg border border-border overflow-hidden bg-background">
      {title && (
        <div className="px-3 py-2 border-b border-border/50 flex items-center gap-2 text-sm">
          <Pencil size={14} className="text-muted-foreground shrink-0" />
          <span className="font-medium truncate">{title}</span>
        </div>
      )}

      <div className="relative overflow-hidden" style={{ maxHeight: 200 }}>
        <div className="absolute inset-x-0 top-0 h-6 bg-linear-to-b from-background to-transparent pointer-events-none z-10" />
        <div className="pt-3 pb-6 flex flex-col divide-y divide-border/40">
          {edits.map((edit, i) => (
            <div key={edit.id ?? i} className="text-xs">
              {/* Removed — red bg, normal text */}
              <div className="px-4 py-1.5 bg-red-150 dark:bg-red-950/30 prose prose-xs dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                <ChatMarkdown>{edit.old_string}</ChatMarkdown>
              </div>
              {/* Added — green bg, normal text */}
              <div className="px-4 py-1.5 bg-green-150 dark:bg-green-950/30 prose prose-xs dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                <ChatMarkdown>{edit.new_string}</ChatMarkdown>
              </div>
            </div>
          ))}
        </div>
        <div className="absolute inset-x-0 bottom-0 h-10 bg-linear-to-t from-background to-transparent pointer-events-none" />
      </div>

      <OpenButton documentId={documentId} />
    </div>
  )
}

// ─── Write (full rewrite) ─────────────────────────────────────────────────────

function WriteDocumentCard({ args, title, documentId }) {
  return (
    <div className="my-3 rounded-lg border border-border overflow-hidden bg-background">
      {title && (
        <div className="px-3 py-2 border-b border-border/50 flex items-center gap-2 text-sm">
          <RefreshCw size={14} className="text-muted-foreground shrink-0" />
          <span className="font-medium truncate">{title}</span>
        </div>
      )}
      {args.content && (
        <div className="bg-green-50 dark:bg-green-950/20">
          <ContentPreview content={args.content} maxHeight={140} topFade />
        </div>
      )}
      <OpenButton documentId={documentId} />
    </div>
  )
}
